/**
 * SafeSystemAI Chat API client. Every call sends `X-Api-Key` (and `X-Session-ID`
 * once known) and turns non-2xx responses into a typed `AiApiError`.
 *
 * Auth: the AI Chat API accepts the same SafeSystem key validated against
 * GeoServer by `apiKeyStore.validateAndSave`. `validate-api-key` additionally
 * issues a per-page `session_id` threaded through later calls via `X-Session-ID`.
 */

import { aiChatApiUrl } from '#lib/config.js';

export class AiApiError extends Error {
	constructor(
		readonly status: number,
		readonly statusText: string,
		readonly body: unknown
	) {
		super(`AI API ${status} ${statusText}`);
		this.name = 'AiApiError';
	}
}

interface RequestContext {
	apiKey: string;
	sessionId?: string | null;
	// Lets callers cancel a request the user has walked away from, and applies
	// the timeout the web-native guidance in CLAUDE.md asks for.
	signal?: AbortSignal;
}

type AgentContext = RequestContext & { chatId: string | null; uiState?: Record<string, unknown> };

/**
 * Thrown when the streaming endpoint is not there to talk to (missing route, or
 * a proxy that will not stream). Distinct from AiApiError so callers can retry
 * non-streaming for this case only, and never for a failure that happened after
 * the agent had already begun executing its tool calls.
 */
export class AgentStreamUnsupportedError extends Error {
	constructor(readonly status: number) {
		super(`agent stream unsupported (${status})`);
		this.name = 'AgentStreamUnsupportedError';
	}
}

function headers(ctx: RequestContext, accept = 'application/json'): Record<string, string> {
	return {
		'Content-Type': 'application/json',
		Accept: accept,
		'X-Api-Key': ctx.apiKey,
		...(ctx.sessionId ? { 'X-Session-ID': ctx.sessionId } : {})
	};
}

// JSON.stringify drops the undefined fields, so a new chat sends no chat_id.
const chatBody = (prompt: string, chatId: string | null, uiState?: Record<string, unknown>) => ({
	query: prompt,
	chat_id: chatId || undefined,
	ui_state: uiState
});

async function postJson<T>(path: string, body: unknown, ctx: RequestContext): Promise<T> {
	const response = await fetch(`${aiChatApiUrl}${path}`, {
		method: 'POST',
		headers: headers(ctx),
		body: JSON.stringify(body),
		signal: ctx.signal
	});

	return parseResponse<T>(response);
}

async function parseResponse<T>(response: Response): Promise<T> {
	const text = await response.text();
	let body: unknown = null;
	if (text) {
		try {
			body = JSON.parse(text);
		} catch {
			body = text;
		}
	}
	if (!response.ok) {
		throw new AiApiError(response.status, response.statusText, body);
	}
	return body as T;
}

// --- Endpoints ----------------------------------------------------------------

export interface ValidateApiKeyResponse {
	session_id: string;
}

/**
 * Exchange the SafeSystem api key for an AI-side `session_id`. The key is
 * already validated against GeoServer by this point; this is the AI-side
 * handshake.
 */
export async function validateApiKey(
	apiKey: string,
	signal?: AbortSignal
): Promise<ValidateApiKeyResponse> {
	return postJson<ValidateApiKeyResponse>(
		'/validate-api-key',
		{ api_key: apiKey },
		{ apiKey, signal }
	);
}

export interface QueryResponse {
	response: string;
	chat_id: string;
}

/**
 * Send a prompt to the methodology Q&A endpoint. The optional `chatId` lets
 * the backend stitch turns into one chat; omit it to start a fresh
 * conversation server-side.
 */
export async function query(
	prompt: string,
	ctx: RequestContext & { chatId: string | null }
): Promise<QueryResponse> {
	// TODO: T1 — on 401, re-handshake via validateApiKey() and retry once. See TODO.md.
	return postJson<QueryResponse>('/query/function-calling', chatBody(prompt, ctx.chatId), ctx);
}

interface ToolActivity {
	tool: string;
	input: Record<string, unknown>;
}

/**
 * One client-side action the agent asked the app to perform, discriminated on
 * `type`. Only `parseUiAction` may produce these: the agent's payload arrives
 * as unvalidated JSON, and an index signature made every field access
 * type-check, so a renamed or wrong-typed field failed at runtime instead of
 * compile time.
 */
export type UiAction =
	| { type: 'switch_view'; view: string }
	| { type: 'switch_module'; module: string }
	| { type: 'set_map_filters' }
	| { type: 'zoom_map'; bbox: number[] }
	| {
			type: 'show_only_features';
			layer_name: string;
			ids?: (number | string)[];
			match_all?: boolean;
			label?: string;
			matched_total?: number;
			feature_points?: Record<string, [number, number]>;
			bbox?: number[];
			zoom?: boolean;
	  }
	| { type: 'hide_layers'; layer_names: string[] }
	| { type: 'clear_data_filter' }
	| {
			type: 'highlight_features';
			geojson: GeoJSON.FeatureCollection;
			bbox?: number[];
			zoom?: boolean;
			label?: string;
	  }
	| { type: 'clear_highlights' };

type RawAction = Record<string, unknown>;

const isRecord = (v: unknown): v is RawAction =>
	typeof v === 'object' && v !== null && !Array.isArray(v);
const asString = (v: unknown) => (typeof v === 'string' ? v : undefined);
const asNumber = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
const asBoolean = (v: unknown) => (typeof v === 'boolean' ? v : undefined);
const asBbox = (v: unknown) =>
	Array.isArray(v) && v.length === 4 && v.every((n) => typeof n === 'number')
		? (v as number[])
		: undefined;
const asStringList = (v: unknown) =>
	Array.isArray(v) && v.every((n) => typeof n === 'string') ? (v as string[]) : undefined;
const asIdList = (v: unknown) =>
	Array.isArray(v) && v.every((n) => typeof n === 'number' || typeof n === 'string')
		? (v as (number | string)[])
		: undefined;

/** Drops entries that aren't a [lon, lat] pair rather than failing the action. */
function asPointMap(v: unknown): Record<string, [number, number]> | undefined {
	if (!isRecord(v)) return undefined;
	const out: Record<string, [number, number]> = {};
	for (const [key, point] of Object.entries(v)) {
		if (
			Array.isArray(point) &&
			point.length === 2 &&
			typeof point[0] === 'number' &&
			typeof point[1] === 'number'
		) {
			out[key] = [point[0], point[1]];
		}
	}
	return out;
}

/**
 * Validate one action from the agent. Returns null when the type is unknown or
 * a field the applier depends on is missing or the wrong shape, so a bad action
 * is skipped rather than throwing mid-way through a batch.
 */
export function parseUiAction(raw: unknown): UiAction | null {
	if (!isRecord(raw)) return null;
	switch (raw.type) {
		case 'switch_view': {
			const view = asString(raw.view);
			return view ? { type: 'switch_view', view } : null;
		}
		case 'switch_module': {
			const module = asString(raw.module);
			return module ? { type: 'switch_module', module } : null;
		}
		case 'set_map_filters':
			return { type: 'set_map_filters' };
		case 'zoom_map': {
			const bbox = asBbox(raw.bbox);
			return bbox ? { type: 'zoom_map', bbox } : null;
		}
		case 'show_only_features': {
			const layerName = asString(raw.layer_name);
			if (!layerName) return null;
			return {
				type: 'show_only_features',
				layer_name: layerName,
				ids: asIdList(raw.ids),
				match_all: asBoolean(raw.match_all),
				label: asString(raw.label),
				matched_total: asNumber(raw.matched_total),
				feature_points: asPointMap(raw.feature_points),
				bbox: asBbox(raw.bbox),
				zoom: asBoolean(raw.zoom)
			};
		}
		case 'hide_layers': {
			const layerNames = asStringList(raw.layer_names);
			return layerNames ? { type: 'hide_layers', layer_names: layerNames } : null;
		}
		case 'clear_data_filter':
			return { type: 'clear_data_filter' };
		case 'highlight_features': {
			// The one remaining cast, and the only validated one: confirm it is a
			// feature collection shape before trusting GeoJSON's types.
			if (!isRecord(raw.geojson) || !Array.isArray(raw.geojson.features)) return null;
			return {
				type: 'highlight_features',
				geojson: raw.geojson as unknown as GeoJSON.FeatureCollection,
				bbox: asBbox(raw.bbox),
				zoom: asBoolean(raw.zoom),
				label: asString(raw.label)
			};
		}
		case 'clear_highlights':
			return { type: 'clear_highlights' };
		default:
			return null;
	}
}

export interface AgentQueryResponse extends QueryResponse {
	tool_activity: ToolActivity[];
	// Unvalidated on the wire; run each through parseUiAction before use.
	ui_actions: unknown[];
}

/**
 * Send a prompt to the data-agent endpoint. The agent answers by running live
 * WFS queries against the platform data with the caller's own key; the
 * returned `tool_activity` trail describes the queries it made and
 * `ui_actions` carries app actions (highlight/zoom/filter/module) to apply on
 * arrival. `uiState` tells the agent what the app currently shows.
 */
export async function queryAgent(prompt: string, ctx: AgentContext): Promise<AgentQueryResponse> {
	return postJson<AgentQueryResponse>(
		'/query/agent',
		chatBody(prompt, ctx.chatId, ctx.uiState),
		ctx
	);
}

/** Progress events emitted by the streaming data-agent endpoint. */
export type AgentStreamEvent =
	| { type: 'status'; message: string }
	| ({ type: 'tool' } & ToolActivity)
	| { type: 'error'; message: string };

/**
 * Streaming variant of `queryAgent`: POSTs to `/query/agent/stream` and
 * parses the Server-Sent Event frames, invoking `onEvent` for each progress
 * event (`status` / `tool`) as the agent works. Resolves with the final
 * result payload (same shape as the non-streaming endpoint).
 */
export async function queryAgentStream(
	prompt: string,
	ctx: AgentContext,
	onEvent: (event: AgentStreamEvent) => void
): Promise<AgentQueryResponse> {
	const response = await fetch(`${aiChatApiUrl}/query/agent/stream`, {
		method: 'POST',
		headers: headers(ctx, 'text/event-stream'),
		body: JSON.stringify(chatBody(prompt, ctx.chatId, ctx.uiState)),
		signal: ctx.signal
	});
	// Only a missing route or a non-streaming response is worth retrying without
	// streaming; anything else means the agent may already have done its work.
	if (response.status === 404 || response.status === 405) {
		throw new AgentStreamUnsupportedError(response.status);
	}
	if (!response.ok) {
		const text = await response.text().catch(() => '');
		throw new AiApiError(response.status, response.statusText, text);
	}
	if (!response.body) {
		throw new AgentStreamUnsupportedError(response.status);
	}

	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	let buffer = '';
	let result: AgentQueryResponse | null = null;

	const handleFrame = (frame: string) => {
		for (const line of frame.split('\n')) {
			if (!line.startsWith('data: ')) continue;
			const event = JSON.parse(line.slice(6)) as
				AgentStreamEvent | ({ type: 'result' } & AgentQueryResponse);
			if (event.type === 'result') {
				result = event;
			} else if (event.type === 'error') {
				throw new AiApiError(500, 'stream error', event.message);
			} else {
				onEvent(event);
			}
		}
	};

	for (;;) {
		const { done, value } = await reader.read();
		if (done) break;
		buffer += decoder.decode(value, { stream: true });
		const frames = buffer.split('\n\n');
		buffer = frames.pop() ?? '';
		for (const frame of frames) handleFrame(frame);
	}
	if (buffer.trim()) handleFrame(buffer);

	if (!result) {
		throw new AiApiError(0, 'stream ended without a result', null);
	}
	return result;
}

/**
 * Tell the backend the chat is finished so it can release whatever state it
 * holds. No-op when there's no current chatId.
 */
export async function endChat(ctx: RequestContext & { chatId: string }): Promise<unknown> {
	return postJson<unknown>('/end-chat', { chat_id: ctx.chatId }, ctx);
}
